import { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  Search as SearchIcon,
  BookOpen,
  FolderTree,
  LogOut,
  Users,
  Smartphone,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  adminApi,
  type ActivitySummary,
  type ActivityEventRow,
} from '@/services/api';

/** Libellé lisible d'un nom d'événement d'activité. */
const EVENT_LABEL: Record<string, string> = {
  exam_view: "Consultation d'examen",
  search: 'Recherche',
  category_select: 'Clic catégorie',
  practice_start: 'Début de pratique',
  simulation_start: 'Début de simulation',
  quiz_abandon: 'Abandon de quiz',
  super_category_select: 'Super-catégorie',
  exam_time_spent: 'Temps sur examen',
};

function eventLabel(name: string): string {
  return EVENT_LABEL[name] ?? name;
}

const DAYS_OPTIONS = [7, 30, 90];
const FEED_PAGE_SIZE = 20;

export function ActivityPage() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<ActivitySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Flux paginé (chargé indépendamment du résumé pour économiser la data).
  const [recent, setRecent] = useState<ActivityEventRow[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [feedLoading, setFeedLoading] = useState(true);

  const load = () => {
    setLoading(true);
    setError(null);
    adminApi
      .getActivitySummary(days)
      .then((s) => setSummary(s.data))
      .catch(() => setError("Impossible de charger l'activité."))
      .finally(() => setLoading(false));
  };

  useEffect(load, [days]);

  const fetchFeed = (p: number) => {
    setFeedLoading(true);
    adminApi
      .getActivityRecent(p, FEED_PAGE_SIZE)
      .then((r) => {
        setRecent(r.data.items);
        setTotal(r.data.total);
      })
      .catch(() => {
        setRecent([]);
        setTotal(0);
      })
      .finally(() => setFeedLoading(false));
  };

  // Feed : rechargé à chaque changement de page.
  useEffect(() => { fetchFeed(page); }, [page]);

  const totalPages = Math.max(1, Math.ceil(total / FEED_PAGE_SIZE));

  const maxEvent = useMemo(
    () => (summary?.byEvent.length ? summary.byEvent[0].count : 1),
    [summary],
  );

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Activité des utilisateurs</h1>
          <p className="text-gray-500 dark:text-gray-400">
            Suivi première partie (hébergé chez nous, sans Google Analytics)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
          >
            {DAYS_OPTIONS.map((d) => (
              <option key={d} value={d}>{d} derniers jours</option>
            ))}
          </select>
          <button
            onClick={() => { load(); fetchFeed(page); }}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading || feedLoading ? 'animate-spin' : ''}`} /> Actualiser
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      {summary && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi icon={<Activity className="w-5 h-5" />} label="Événements" value={summary.totalEvents.toLocaleString('fr-FR')} tint="emerald" />
            <Kpi icon={<Users className="w-5 h-5" />} label="Utilisateurs actifs" value={String(summary.activeUsers)} tint="blue" />
            <Kpi icon={<LogOut className="w-5 h-5" />} label="Abandons de quiz" value={String(summary.abandon.count)} tint="amber" />
            <Kpi icon={<LogOut className="w-5 h-5" />} label="Q. avant abandon (moy.)" value={String(summary.abandon.avgAnswered)} tint="rose" />
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {/* Répartition par événement */}
            <Card title="Que font les utilisateurs" icon={<Activity className="w-5 h-5 text-emerald-500" />}>
              {summary.byEvent.length === 0 ? (
                <Empty />
              ) : (
                <div className="space-y-2">
                  {summary.byEvent.map((e) => (
                    <div key={e.name}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-700 dark:text-gray-200">{eventLabel(e.name)}</span>
                        <span className="text-gray-500 tabular-nums">{e.count.toLocaleString('fr-FR')}</span>
                      </div>
                      <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.max(3, (e.count / maxEvent) * 100)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Examens les plus sollicités (vues) */}
            <Card title="Examens les plus consultés" icon={<BookOpen className="w-5 h-5 text-blue-500" />}>
              {summary.topExams.length === 0 ? <Empty /> : (
                <ol className="space-y-2">
                  {summary.topExams.map((x, i) => (
                    <li key={x.examId} className="flex items-center gap-3 text-sm">
                      <span className="w-5 text-gray-400 tabular-nums">{i + 1}</span>
                      <span className="flex-1 truncate text-gray-800 dark:text-gray-100">{x.examName}</span>
                      <span className="text-gray-500 tabular-nums">{x.views}</span>
                    </li>
                  ))}
                </ol>
              )}
            </Card>

            {/* Recherches */}
            <Card title="Recherches les plus fréquentes" icon={<SearchIcon className="w-5 h-5 text-violet-500" />}>
              {summary.topSearches.length === 0 ? <Empty /> : (
                <div className="flex flex-wrap gap-2">
                  {summary.topSearches.map((s) => (
                    <span key={s.term} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-xs">
                      {s.term} <span className="text-violet-400">×{s.count}</span>
                    </span>
                  ))}
                </div>
              )}
            </Card>

            {/* Catégories */}
            <Card title="Catégories les plus cliquées" icon={<FolderTree className="w-5 h-5 text-amber-500" />}>
              {summary.topCategories.length === 0 ? <Empty /> : (
                <ol className="space-y-2">
                  {summary.topCategories.map((c, i) => (
                    <li key={c.categoryId} className="flex items-center gap-3 text-sm">
                      <span className="w-5 text-gray-400 tabular-nums">{i + 1}</span>
                      <span className="flex-1 truncate text-gray-800 dark:text-gray-100">{c.categoryName}</span>
                      <span className="text-gray-500 tabular-nums">{c.clicks}</span>
                    </li>
                  ))}
                </ol>
              )}
            </Card>
          </div>

          {/* Flux récent */}
          <Card title="Flux d'activité récent" icon={<Activity className="w-5 h-5 text-gray-500" />}>
            {feedLoading && recent.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">Chargement…</p>
            ) : recent.length === 0 ? <Empty /> : (
              <div className={`overflow-x-auto transition-opacity ${feedLoading ? 'opacity-50' : ''}`}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 border-b border-gray-100 dark:border-gray-700">
                      <th className="py-2 pr-3 font-medium">Quand</th>
                      <th className="py-2 pr-3 font-medium">Utilisateur</th>
                      <th className="py-2 pr-3 font-medium">Action</th>
                      <th className="py-2 pr-3 font-medium">Détail</th>
                      <th className="py-2 font-medium">Appareil</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((e) => (
                      <tr key={e.id} className="border-b border-gray-50 dark:border-gray-700/50">
                        <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">{new Date(e.createdAt).toLocaleString('fr-FR')}</td>
                        <td className="py-2 pr-3 text-gray-700 dark:text-gray-200 truncate max-w-[160px]">
                          {e.isGuest ? '👤 Invité' : (e.userEmail ?? '—')}
                        </td>
                        <td className="py-2 pr-3">{eventLabel(e.name)}</td>
                        <td className="py-2 pr-3 text-gray-500 truncate max-w-[220px]">
                          {e.searchTerm ? `« ${e.searchTerm} »` : e.examName ?? e.categoryName ?? (e.numValue != null ? String(e.numValue) : '—')}
                        </td>
                        <td className="py-2 text-gray-500">
                          {e.deviceCategory && <span className="inline-flex items-center gap-1"><Smartphone className="w-3 h-3" />{e.deviceCategory}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {total > 0 && (
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 text-sm">
                <span className="text-gray-500">
                  {total.toLocaleString('fr-FR')} événement{total > 1 ? 's' : ''} · page {page}/{totalPages}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || feedLoading}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <ChevronLeft className="w-4 h-4" /> Précédent
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages || feedLoading}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Suivant <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function Kpi({ icon, label, value, tint }: { icon: React.ReactNode; label: string; value: string; tint: string }) {
  const tints: Record<string, string> = {
    emerald: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    rose: 'bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400',
  };
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tints[tint]}`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xl font-bold text-gray-900 dark:text-white truncate">{value}</p>
        <p className="text-xs text-gray-500 truncate">{label}</p>
      </div>
    </div>
  );
}

function Card({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5">
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Empty() {
  return <p className="text-sm text-gray-400 py-4 text-center">Aucune donnée sur la période.</p>;
}
